import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { articles, categories } from '@/mocks/articles';
import { siteInfo } from '@/mocks/siteInfo';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 4);
  }, [article]);

  const category = article ? categories.find((c) => c.slug === article.category) : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="chigua-container pt-24 pb-16 text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">返回首页</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://itxkf.cn/article/${article.id}`,
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
    { label: article.title },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <article className="chigua-container">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {article.isNew && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50 whitespace-nowrap">
                      最新
                    </span>
                  )}
                  {article.isHot && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
                      热搜
                    </span>
                  )}
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap no-underline"
                    >
                      {category.name}
                    </Link>
                  )}
                </div>

                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {article.publishedAt}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.views > 10000
                      ? `${(article.views / 10000).toFixed(1)}万 阅读`
                      : `${article.views} 阅读`}
                  </span>
                </div>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  className="w-full aspect-[16/9] object-cover"
                  title={`${article.title} - 吃瓜黑料`}
                />
              </div>

              <div
                className="prose prose-lg max-w-none mb-10 text-foreground-800 leading-relaxed article-content"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex items-center gap-2 mb-10 flex-wrap">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200 whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="border-t border-background-200 pt-10">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    相关推荐
                  </h2>
                  {category && (
                    <Link
                      to={`/category/${category.slug}`}
                      className="text-sm text-foreground-500 hover:text-primary-600 transition-colors no-underline"
                    >
                      更多{category.name} <i className="ri-arrow-right-line"></i>
                    </Link>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {relatedArticles.map((ra) => (
                    <ArticleCard key={ra.id} article={ra} />
                  ))}
                </div>
              </div>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
}